import { useEffect } from 'react';

// Body classes the Vortala stylesheet keys layout on (`home`, `not-home`,
// `page-<slug>`, `fullscript-promo-active`). They are applied in effects on
// the client; during SSR (no document) the same calls register the classes
// here so the prerender can emit them on <body> — otherwise first paint
// lays out without them and shifts when hydration's effects add them.
const ssrBodyClasses = new Set<string>();

export function registerSsrBodyClasses(classes: readonly string[]) {
  if (typeof document !== 'undefined') return;
  for (const c of classes) ssrBodyClasses.add(c);
}

/** Server-only: returns and clears the classes registered by the last render. */
export function drainSsrBodyClasses(): string[] {
  const classes = [...ssrBodyClasses];
  ssrBodyClasses.clear();
  return classes;
}

/**
 * Adds the classes to <body> for the component's lifetime (and registers
 * them for the SSR body tag). `removeFirst` strips classes another page may
 * have left behind (interior pages remove `home`).
 */
export function useBodyClass(classes: string[], removeFirst: string[] = []) {
  registerSsrBodyClasses(classes);
  useEffect(() => {
    removeFirst.forEach((c) => document.body.classList.remove(c));
    classes.forEach((c) => document.body.classList.add(c));
    return () => {
      classes.forEach((c) => document.body.classList.remove(c));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes]);
}
